import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Box,
  CircularProgress,
  Slide,
  Typography,
  Divider,
  Stack,
  Chip,
  Alert,
} from "@mui/material";
import {
  PaymentOutlined,
  CloseOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import PaypalBtns from "../PaymentFolder/Paypal";
import PaymobBtns from "../PaymentFolder/Paymob";
import CheckoutBtns from "../PaymentFolder/CheckoutBtns";

// Transition component for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Test card details component for demo purposes
 */
const TestCardDetails = ({ eventId, paymentMethod }) => {
  // Only show test card details for eventId === 7493635113
  if (eventId !== "7493635113") {
    return null;
  }

  const getTestCardDetails = () => {
    switch (paymentMethod) {
      case "paymob":
        return {
          title: "PayMob Demo Sandbox Details",
          details: [
            { label: "Card Type", value: "VISA" },
            { label: "Card Number", value: "4111111111111111" },
            { label: "Cardholder Name", value: "Test Account" },
            { label: "Expiry Month", value: "12" },
            { label: "Expiry Year", value: "25" },
            { label: "CVV", value: "123" },
          ],
        };
      case "checkout":
      default:
        return {
          title: "Test Card Details",
          details: [
            { label: "Country", value: "United Kingdom" },
            { label: "Card Number", value: "4698466893517435" },
            { label: "Expiry Date", value: "04/2027" },
            { label: "CVV", value: "197" },
            { label: "Zip Code", value: "AB10 6DN" },
            { label: "Mobile", value: "86227 27272" },
          ],
        };
    }
  };

  const testCardInfo = getTestCardDetails();

  return (
    <Alert
      severity="info"
      icon={<InfoOutlined />}
      sx={{
        mb: 3,
        backgroundColor: "#e3f2fd",
        border: "1px solid #1976d2",
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {testCardInfo.title}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {testCardInfo.details.map((detail, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "0.875rem",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {detail.label}:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                backgroundColor: "#f5f5f5",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.8rem",
              }}
            >
              {detail.value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography
        variant="caption"
        sx={{ mt: 1, display: "block", fontStyle: "italic" }}
      >
        Note: "Save information & create your PayPal account" is disabled for
        testing
      </Typography>
    </Alert>
  );
};

/**
 * Dialog for payment processing with support for multiple payment methods
 */
const PaymentSummaryDialog = ({
  open,
  handleClose,
  price,
  selectedCurrency,
  clientId,
  secretKey,
  publicKey,
  handlePaymentSuccess,
  allData,
  selectedPaymentOption,
  base,
  setErrorInPaymentInitialization,
  eventId,
  role,
  setIsPopupNotOpened,
  link,
}) => {
  // Determine dialog size based on payment method
  const isFullScreen =
    determineDialogSize(selectedPaymentOption, allData) === "fullScreen";
  const isFullWidth =
    determineDialogSize(selectedPaymentOption, allData) === "fullWidth";

  /**
   * Determines dialog size based on payment method
   */
  function determineDialogSize(paymentOption, data) {
    if (paymentOption === "paypal" || data?.paymentMethod === "paypal") {
      return "standard"; // Use standard size for enhanced PayPal UI
    }
    return "fullWidth";
  }

  /**
   * Renders the PayPal payment option with enhanced UI
   */
  function renderPaypal() {
    return (
      <Box sx={{ maxWidth: 500, mx: "auto" }}>
        {/* Payment Summary Header */}
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <PaymentOutlined sx={{ color: "#1976d2", fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#2c3e50" }}>
              Complete Payment
            </Typography>
          </Stack>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {allData?.payment || "Registration Payment"}
          </Typography>
        </Box>

        {/* Test Card Details */}
        <TestCardDetails eventId={eventId} paymentMethod="paypal" />

        {/* Payment Details Table */}
        <Paper elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 600, py: 2 }}
                  >
                    <Typography variant="body1">Registration Fee</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2 }}>
                    <Chip
                      label={allData?.payment || `${price} ${selectedCurrency}`}
                      sx={{
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 600, py: 2 }}
                  >
                    <Typography variant="body1">Total Amount</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "#2e7d32" }}
                    >
                      {price || 0} {selectedCurrency}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Enhanced PayPal Component */}
        <PaypalBtns
          currency={selectedCurrency}
          amount={price || 0}
          onPaymentSuccess={handlePaymentSuccess}
          buyerEmail={allData?.Email || allData?.email}
          clientId={clientId}
          description={allData?.payment}
          base={base}
          eventId={eventId}
        />
      </Box>
    );
  }

  /**
   * Renders the Paymob payment option
   */
  function renderPaymob() {
    return (
      <Box>
        {/* Test Card Details */}
        <TestCardDetails eventId={eventId} paymentMethod="paymob" />

        <PaymobBtns
          currency={selectedCurrency}
          amount={price || 0}
          onPaymentSuccess={handlePaymentSuccess}
          buyerEmail={allData?.Email || allData?.email}
          clientId={clientId}
          description={allData?.payment}
          base={base}
          setErrorInPaymentInitialization={setErrorInPaymentInitialization}
          setIsPopupNotOpened={setIsPopupNotOpened}
          secretKey={secretKey}
          publicKey={publicKey}
          allData={allData}
          eventId={eventId}
        />
      </Box>
    );
  }

  /**
   * Renders the Checkout payment option
   */
  function renderCheckout() {
    return (
      <Box>
        {/* Test Card Details */}
        <TestCardDetails eventId={eventId} paymentMethod="checkout" />

        <CheckoutBtns
          currency={selectedCurrency}
          amount={price || 0}
          onPaymentSuccess={handlePaymentSuccess}
          buyerEmail={allData?.Email || allData?.email}
          clientId={clientId}
          description={allData?.payment}
          base={base}
          setErrorInPaymentInitialization={setErrorInPaymentInitialization}
          setIsPopupNotOpened={setIsPopupNotOpened}
          secretKey={secretKey}
          publicKey={publicKey}
          allData={allData}
          eventId={eventId}
          role={role}
          link={link}
        />
      </Box>
    );
  }

  /**
   * Renders the appropriate payment method
   */
  function renderPaymentMethod() {
    if (
      selectedPaymentOption === "paypal" ||
      allData?.paymentMethod === "paypal"
    ) {
      return renderPaypal();
    } else if (
      selectedPaymentOption === "paymob" ||
      allData?.paymentMethod === "paymob"
    ) {
      return renderPaymob();
    } else if (
      selectedPaymentOption === "checkout" ||
      allData?.paymentMethod === "checkout"
    ) {
      return renderCheckout();
    }
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      keepMounted
      aria-describedby="payment-dialog-description"
      fullScreen={isFullScreen}
      fullWidth={isFullWidth}
      maxWidth={selectedPaymentOption === "paypal" ? "sm" : "md"}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: selectedPaymentOption === "paypal" ? 3 : 1,
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
      }}
    >
      {/* Custom Header for PayPal */}
      {(selectedPaymentOption === "paypal" ||
        allData?.paymentMethod === "paypal") && (
        <Box
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            p: 2,
            color: "white",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Secure Payment
            </Typography>
            <Button
              onClick={handleClose}
              sx={{
                color: "white",
                minWidth: "auto",
                p: 1,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <CloseOutlined />
            </Button>
          </Stack>
        </Box>
      )}

      <DialogContent
        sx={{
          p: selectedPaymentOption === "paypal" ? 3 : 2,
          backgroundColor:
            selectedPaymentOption === "paypal" ? "#f8f9fa" : "inherit",
        }}
      >
        {!clientId || !secretKey ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress color="primary" size={40} />
            <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
              Loading payment options...
            </Typography>
          </Box>
        ) : (
          renderPaymentMethod()
        )}
      </DialogContent>

      {/* Close button for PayPal */}
      {(selectedPaymentOption === "paypal" ||
        allData?.paymentMethod === "paypal") && (
        <DialogActions sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: "#ccc",
              color: "#666",
              "&:hover": {
                borderColor: "#999",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PaymentSummaryDialog;
